// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"

/**
 * Combobox Controller
 * @gem rapid_rails_ui
 * @version 0.31.0
 * @updated 2026-01-17
 *
 * A slim controller for combobox-specific functionality. Designed to be composed
 * with menu, keyboard, and search controllers for full functionality.
 *
 * Composition:
 *   <div data-controller="menu keyboard search combobox"
 *        data-action="keyboard:escape->menu#close search:filter->combobox#onFilter"
 *        data-menu-selection-mode-value="true"
 *        data-keyboard-selector-value="[role='option']:not(.hidden)"
 *        data-search-selector-value="[role='option']"
 *        data-search-attribute-value="data-text">
 *
 * This controller handles:
 *   - Multi-select hidden input management
 *   - Trigger text updates ("X items selected")
 *   - Max selections enforcement
 *   - Option selection with checkbox/radio visual updates
 *
 * For single-select without multi-select features, use menu controller directly.
 *
 * Targets:
 *   - triggerText: Span to show selected text/count
 *   - option: All option elements
 *   - hiddenInput: Hidden form inputs
 *   - checkbox: Checkbox inputs (multi-select)
 *   - radio: Radio inputs (single-select)
 *   - emptyInput: Empty input for array submission
 *   - noResults: "No results" message element
 *
 * Values:
 *   - multiple: Boolean - Allow multiple selection
 *   - maxSelections: Number - Limit selections (multi-select only)
 *   - closeOnSelect: Boolean - Close after selection
 *   - placeholder: String - Default placeholder text
 */
export default class extends Controller {
  static targets = [
    "triggerText",
    "option",
    "hiddenInput",
    "checkbox",
    "radio",
    "emptyInput",
    "noResults",
    "checkmark",
    "groupHeader",
    "badgesContainer",
    "searchInput"
  ]

  static values = {
    multiple: { type: Boolean, default: false },
    maxSelections: { type: Number, default: 0 },
    closeOnSelect: { type: Boolean, default: true },
    clearOnSelect: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select an option" }
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  connect() {
    this.selectedValues = this._getInitialSelected()
    // AUDIT-18: snapshot the server-rendered selection so a native form
    // reset can rebuild our JS-managed state from a known-good baseline
    // (see _handleReset below - native reset can't do this correctly on
    // its own for inputs we add/remove dynamically after connect).
    this._initialSelectedValues = [...this.selectedValues]

    this._handleReset = this._handleReset.bind(this)
    this.formElement = this.element.closest("form")
    this.formElement?.addEventListener("reset", this._handleReset)
  }

  disconnect() {
    this.formElement?.removeEventListener("reset", this._handleReset)
  }

  // Build a target attribute selector that respects this.identifier so that
  // hosts using `config.stimulus_namespace` (e.g. "rui--combobox") still match.
  _targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`
  }

  // ==========================================================================
  // PUBLIC ACTIONS
  // ==========================================================================

  /**
   * Handle option selection (called via click action on options)
   */
  select(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    const text = option.dataset.text
    const isDisabled = option.getAttribute("aria-disabled") === "true"

    if (isDisabled) return

    event.preventDefault()
    event.stopPropagation()

    if (this.multipleValue) {
      this._toggleMultipleSelection(value, text, option)
    } else {
      this._setSingleSelection(value, text, option)
    }

    // AUDIT-17: clear_on_select was accepted/stored/documented but never
    // read by the controller - now that clearOnSelectValue is threaded
    // through from the component, actually act on it.
    this._clearSearchIfNeeded()

    // Dispatch change event
    this._dispatchChangeEvent()

    // Close menu if configured (dispatch to menu controller)
    if (this.closeOnSelectValue && !this.multipleValue) {
      this.dispatch("requestClose", { bubbles: true })
    }
  }

  /**
   * Remove a selection via its badge chip's remove button (AUDIT-16).
   */
  removeBadge(event) {
    event.preventDefault()
    event.stopPropagation()

    const value = event.currentTarget.dataset.value
    if (!this.selectedValues.includes(value)) return

    const option = this.optionTargets.find(opt => opt.dataset.value === value)
    this._removeSelection(value, option)
    this._dispatchChangeEvent()
  }

  /**
   * Clear the search input after a selection when clear_on_select is
   * enabled, and re-run filtering so the full option list is visible again.
   */
  _clearSearchIfNeeded() {
    if (!this.clearOnSelectValue || !this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  /**
   * Handle search filter results (listens to search:filter event)
   */
  onFilter(event) {
    const { visibleCount } = event.detail

    // Show/hide "no results" message
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("hidden", visibleCount > 0)
    }

    // For grouped collections, hide group headers when all their options are hidden
    if (this.hasGroupHeaderTarget) {
      this.groupHeaderTargets.forEach(header => {
        const groupId = header.dataset.groupId
        if (!groupId) return

        const groupOptions = this.optionTargets.filter(
          opt => opt.dataset.groupId === groupId
        )
        const anyVisible = groupOptions.some(opt => !opt.classList.contains("hidden"))
        header.classList.toggle("hidden", !anyVisible)
      })
    }
  }

  /**
   * Select the currently focused option (triggered by Enter key)
   */
  selectFocused(event) {
    const focused = document.activeElement
    const isOption = focused?.getAttribute("role") === "option"

    if (isOption && this.optionTargets.includes(focused)) {
      event.preventDefault()
      // Trigger click on the focused option
      focused.click()
    }
  }

  /**
   * Clear all selections
   */
  clearAll() {
    if (this.multipleValue) {
      // Remove all hidden inputs except empty one
      this.hiddenInputTargets.forEach(input => {
        if (input !== this.emptyInputTarget) {
          input.remove()
        }
      })

      // Reset all options
      this.optionTargets.forEach(option => {
        option.setAttribute("aria-selected", "false")
        const checkbox = option.querySelector(this._targetSelector("checkbox"))
        if (checkbox) checkbox.checked = false
      })

      this.selectedValues = []
      this._updateTriggerText()
      this._updateBadges()
      this._updateMaxSelectionsState()
    } else {
      // Clear the single-select hidden input and unselect all options -
      // previously this branch was a no-op, so the hidden input kept its
      // value even though a change event was dispatched.
      if (this.hasHiddenInputTarget) {
        this.hiddenInputTarget.value = ""
      }

      this.optionTargets.forEach(option => {
        option.setAttribute("aria-selected", "false")
        option.classList.remove("bg-zinc-50", "dark:bg-zinc-700/50")
        const radio = option.querySelector(this._targetSelector("radio"))
        if (radio) radio.checked = false
      })

      this.selectedValues = []
      this._updateTriggerText()
    }

    this._dispatchChangeEvent()
  }

  // ==========================================================================
  // PRIVATE: SINGLE SELECTION
  // ==========================================================================

  _setSingleSelection(value, text, optionElement) {
    // Unselect all options
    this.optionTargets.forEach(opt => {
      opt.setAttribute("aria-selected", "false")
      opt.classList.remove("bg-zinc-50", "dark:bg-zinc-700/50")
      const radio = opt.querySelector(this._targetSelector("radio"))
      if (radio) radio.checked = false
    })

    // Select this option
    optionElement.setAttribute("aria-selected", "true")
    optionElement.classList.add("bg-zinc-50", "dark:bg-zinc-700/50")
    const radio = optionElement.querySelector(this._targetSelector("radio"))
    if (radio) radio.checked = true

    // Update hidden input
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
    }

    this.selectedValues = [value]
    this._updateTriggerText(text)
  }

  // ==========================================================================
  // PRIVATE: MULTIPLE SELECTION
  // ==========================================================================

  _toggleMultipleSelection(value, text, optionElement) {
    const isSelected = this.selectedValues.includes(value)

    if (isSelected) {
      this._removeSelection(value, optionElement)
    } else {
      // Check max limit
      if (this.maxSelectionsValue > 0 && this.selectedValues.length >= this.maxSelectionsValue) {
        return
      }
      this._addSelection(value, text, optionElement)
    }
  }

  _addSelection(value, text, optionElement) {
    this.selectedValues.push(value)

    // Update option UI
    if (optionElement) {
      optionElement.setAttribute("aria-selected", "true")
      const checkbox = optionElement.querySelector(this._targetSelector("checkbox"))
      if (checkbox) checkbox.checked = true

      // Show checkmark
      const checkmark = optionElement.querySelector(this._targetSelector("checkmark"))
      if (checkmark) checkmark.classList.remove("invisible")
    }

    // Add hidden input
    this._addHiddenInput(value)
    this._updateTriggerText()
    this._updateBadges()
    this._updateMaxSelectionsState()
  }

  _removeSelection(value, optionElement) {
    this.selectedValues = this.selectedValues.filter(v => v !== value)

    // Update option UI (optionElement may be absent - e.g. removed via a
    // badge chip's remove button while the option itself is filtered out)
    if (optionElement) {
      optionElement.setAttribute("aria-selected", "false")
      const checkbox = optionElement.querySelector(this._targetSelector("checkbox"))
      if (checkbox) checkbox.checked = false

      // Hide checkmark
      const checkmark = optionElement.querySelector(this._targetSelector("checkmark"))
      if (checkmark) checkmark.classList.add("invisible")
    }

    // Remove hidden input
    this._removeHiddenInput(value)
    this._updateTriggerText()
    this._updateBadges()
    this._updateMaxSelectionsState()
  }

  _addHiddenInput(value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this._getInputName()
    input.value = value
    input.setAttribute(`data-${this.identifier}-target`, "hiddenInput")

    if (this.hasEmptyInputTarget) {
      this.emptyInputTarget.parentNode.insertBefore(input, this.emptyInputTarget.nextSibling)
    } else {
      this.element.appendChild(input)
    }
  }

  _removeHiddenInput(value) {
    const input = this.hiddenInputTargets.find(i => i.value === value)
    if (input && input !== this.emptyInputTarget) {
      input.remove()
    }
  }

  _getInputName() {
    if (this.hasHiddenInputTarget) {
      return this.hiddenInputTarget.name
    }
    if (this.hasEmptyInputTarget) {
      return this.emptyInputTarget.name
    }
    return "selection[]"
  }

  // ==========================================================================
  // PRIVATE: UI UPDATES
  // ==========================================================================

  _updateTriggerText(text = null) {
    if (!this.hasTriggerTextTarget) return

    if (this.multipleValue) {
      const count = this.selectedValues.length
      if (count > 0) {
        this.triggerTextTarget.textContent = `${count} ${count === 1 ? 'item' : 'items'} selected`
        this.triggerTextTarget.classList.remove("text-zinc-400", "dark:text-zinc-500")
      } else {
        this.triggerTextTarget.textContent = this.placeholderValue
        this.triggerTextTarget.classList.add("text-zinc-400", "dark:text-zinc-500")
      }
    } else if (text) {
      this.triggerTextTarget.textContent = text
      this.triggerTextTarget.classList.remove("text-zinc-400", "dark:text-zinc-500")
    } else {
      // No text passed (clear/deselect) - fall back to placeholder instead
      // of leaving the previously selected label on screen.
      this.triggerTextTarget.textContent = this.placeholderValue
      this.triggerTextTarget.classList.add("text-zinc-400", "dark:text-zinc-500")
    }
  }

  _updateMaxSelectionsState() {
    if (this.maxSelectionsValue <= 0) return

    const maxReached = this.selectedValues.length >= this.maxSelectionsValue

    this.optionTargets.forEach(option => {
      // AUDIT-15: options the server permanently disabled must never be
      // re-enabled here just because we're back under max_selections - only
      // options capped purely by the dynamic max_selections limit should
      // toggle.
      if (option.dataset.serverDisabled === "true") return

      const value = option.dataset.value
      const isSelected = this.selectedValues.includes(value)

      if (maxReached && !isSelected) {
        option.setAttribute("aria-disabled", "true")
        option.classList.add("opacity-50", "cursor-not-allowed", "pointer-events-none")
      } else {
        option.setAttribute("aria-disabled", "false")
        option.classList.remove("opacity-50", "cursor-not-allowed", "pointer-events-none")
      }
    })
  }

  /**
   * Rebuild the trigger's badge chips to match selectedValues (AUDIT-16).
   * No-op when the component wasn't rendered in badge mode (no
   * badgesContainer target - single-select or show_badges: false).
   */
  _updateBadges() {
    if (!this.hasBadgesContainerTarget) return

    this.badgesContainerTarget.innerHTML = ""
    this.selectedValues.forEach(value => {
      const option = this.optionTargets.find(opt => opt.dataset.value === value)
      const text = option ? option.dataset.text : value
      this.badgesContainerTarget.appendChild(this._buildBadgeElement(value, text))
    })

    const hasSelection = this.selectedValues.length > 0
    this.badgesContainerTarget.classList.toggle("hidden", !hasSelection)
    if (this.hasTriggerTextTarget) {
      this.triggerTextTarget.classList.toggle("hidden", hasSelection)
    }
  }

  _buildBadgeElement(value, text) {
    const badge = document.createElement("span")
    badge.className = "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 text-xs font-medium text-zinc-700 dark:text-zinc-200"
    badge.dataset.value = value

    const label = document.createElement("span")
    label.textContent = text
    badge.appendChild(label)

    // span[role=button], not <button>: chips live inside the trigger <button>
    // and nested buttons are invalid HTML (matches the server-rendered chip)
    const remove = document.createElement("span")
    remove.setAttribute("role", "button")
    remove.tabIndex = 0
    remove.className = "cursor-pointer text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-500 rounded-sm leading-none"
    remove.setAttribute("aria-label", `Remove ${text}`)
    remove.dataset.value = value
    remove.setAttribute("data-action", `click->${this.identifier}#removeBadge keydown.enter->${this.identifier}#removeBadge keydown.space->${this.identifier}#removeBadge`)
    remove.textContent = "×"
    badge.appendChild(remove)

    return badge
  }

  // ==========================================================================
  // PRIVATE: HELPERS
  // ==========================================================================

  _getInitialSelected() {
    if (!this.hasHiddenInputTarget) return []

    if (this.multipleValue) {
      return this.hiddenInputTargets
        .filter(input => input !== this.emptyInputTarget)
        .map(input => input.value)
        .filter(val => val && val !== "")
    } else {
      const value = this.hiddenInputTarget.value
      return value ? [value] : []
    }
  }

  _dispatchChangeEvent() {
    this.dispatch("change", {
      detail: { values: this.selectedValues },
      bubbles: true
    })
  }

  // ==========================================================================
  // PRIVATE: NATIVE FORM RESET (AUDIT-18)
  // ==========================================================================
  //
  // Native <form> reset only restores form-associated elements to their
  // DOM-parse-time defaultValue. That breaks down for combobox's dynamic
  // hidden inputs two ways: _addHiddenInput() creates a fresh <input> and
  // sets its .value PROPERTY only (never a value ATTRIBUTE), so its
  // defaultValue is "" - a native reset would blank it, not restore it.
  // And _removeHiddenInput() straight-up .remove()s server-rendered
  // inputs, which a native reset can never bring back at all (deleted DOM
  // nodes aren't part of the browser's reset algorithm). So instead of
  // relying on native reset for our own dynamically-managed inputs, we
  // listen for the form's reset event ourselves and rebuild everything -
  // selectedValues, hidden inputs, and visual state - from the selection
  // snapshot captured at connect().

  _handleReset() {
    // Defer one tick so the browser's native reset pass (which does
    // correctly restore any ORIGINAL, still-present form controls) finishes
    // first; our resync then authoritatively overwrites whatever it did to
    // the inputs we manage.
    requestAnimationFrame(() => this._resyncAfterReset())
  }

  _resyncAfterReset() {
    const restoreValues = [...this._initialSelectedValues]

    if (this.multipleValue) {
      this.hiddenInputTargets.forEach(input => {
        if (input !== this.emptyInputTarget) input.remove()
      })
      restoreValues.forEach(value => this._addHiddenInput(value))
    } else if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = restoreValues[0] || ""
    }

    this.selectedValues = restoreValues

    let selectedText = null
    this.optionTargets.forEach(option => {
      const isSelected = restoreValues.includes(option.dataset.value)
      option.setAttribute("aria-selected", isSelected ? "true" : "false")
      if (!this.multipleValue) {
        option.classList.toggle("bg-zinc-50", isSelected)
        option.classList.toggle("dark:bg-zinc-700/50", isSelected)
      }

      const checkbox = option.querySelector(this._targetSelector("checkbox"))
      if (checkbox) checkbox.checked = isSelected
      const radio = option.querySelector(this._targetSelector("radio"))
      if (radio) radio.checked = isSelected
      const checkmark = option.querySelector(this._targetSelector("checkmark"))
      if (checkmark) checkmark.classList.toggle("invisible", !isSelected)

      if (isSelected && !this.multipleValue) selectedText = option.dataset.text
    })

    this._updateTriggerText(selectedText)
    this._updateBadges()
    this._updateMaxSelectionsState()
  }
}
